<template>
<div class="vld-parent">
    <loading 
        :active="loading"
        :can-cancel="false" 
        :is-full-page="false"
    />

    <p v-if="pagination">
        <small v-if="items.length > 0">
        {{$t("general:showing")}} {{(page - 1) * items_per_page + 1 }} {{$t("general:to")}} {{(page - 1) * items_per_page + paginated_items.length }} {{$t("general:of_all")}} {{ items.length }} {{$t("analysis:ct:transactions")}}
        </small>
        <small v-else>
        {{$t("general:search:no_results")}}
        </small>
    </p>

    <table class="table text-center table-striped m-0">
        <thead>
            <tr>
                <th
                    v-for="col in columns"
                    v-bind:key="col.id"
                    class="text-center"
                    v-bind:class="getSortedClass(col.sort_key)"
                >
                    <a href="#" v-if="sortable&&col.sort_key" v-on:click.prevent="sortBy(col.sort_key)">{{$t(col.display_name)}}</a>
                    <span v-else>{{$t(col.display_name)}}</span>
                    <help-icon v-if="col.help_icon_slug" class="m-l-5" :slug="col.help_icon_slug" />
                </th>
            </tr>
            <tr v-if="show_deselect_controls && has_selected_items">
                <th
                    v-for="col in columns"
                    v-bind:key="'ctrl-'+col.display_name"
                    class="p-5 text-center"
                >
                    <div v-if="col.key === 'options' && col.display_name === 'analysis:ct:selected_comparable'" class="btn-group dropdown">
                        <a href="#" class="btn btn-xs dropdown-toggle" data-toggle="dropdown">
                            <i class="fa" :class="comparable_checkbox_class"></i>
                            <span class="caret"></span>
                        </a>
                        <ul class="dropdown-menu dropdown-menu-right" role="menu">
                            <li>
                                <a href="#" @click.prevent="$emit('deselect-all', 'comparable')">
                                    {{$t("analysis:ct:deselect_all")}}
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div v-else-if="col.key === 'options' && col.display_name === 'analysis:ct:wider_range'" class="btn-group dropdown">
                        <a href="#" class="btn btn-xs dropdown-toggle" data-toggle="dropdown">
                            <i class="fa" :class="wide_set_checkbox_class"></i>
                            <span class="caret"></span>
                        </a>
                        <ul class="dropdown-menu dropdown-menu-right" role="menu">
                            <li>
                                <a href="#" @click.prevent="$emit('deselect-all', 'wide_set')">
                                    {{$t("analysis:ct:deselect_all")}}
                                </a>
                            </li>
                        </ul>
                    </div>
                </th>
            </tr>
        </thead>

        <tbody v-if="show_main_property_row">
            <main-property-row
                :session="session"
                :columns="columns"
            />
        </tbody>

        <tbody>
            <template v-if="!loading && paginated_items.length == 0">
                <td :colspan="columns.length">
                    <p>{{no_items_text}}</p>
                </td>
            </template>

            <results-row
                v-for="item, inx in paginated_items"
                v-bind:key="`${item.id}-${inx}`"
                :item="item"
                :columns="columns"
                :row_expanded="showAdditionalItems(item.transaction_id)"
                @labelclicked="toggleAdditionalTransactionitems(item.transaction_id)"
                @comparable-clicked="$emit('comparable-clicked', $event);"
                @close-fullscreen="$emit('close-fullscreen');"
            >
            </results-row>

            <template>
                <slot name="additional-rows" v-bind:colspan="columns.length">
                </slot>
            </template>
        </tbody>
    </table>
    
    <div class="text-center" v-if="pagination">
        <pagination
            v-model="page"
            :records="items.length"
            :per-page="items_per_page"
            :options="{
                edgeNavigation: true
            }"
        />
    </div>
</div>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import Pagination from 'vue-pagination-2';
import HelpIcon from "@/components/widgets/HelpIcon";

import Vue from "vue";

import ApiService from "@/components/utils/api.service.js"
import Utils from "@/components/utils/utils.js"

import ResultsRow from "./ResultsTableRow";
import MainPropertyRow from "./MainPropertyRow";

import { all_unit_columns, all_parcel_columns } from "./columns";

import countrymixin from "@/mixins/countrymixin";

export default {
    mixins: [ countrymixin ],
    props:  {
        show_main_property_row: {
            type: Boolean,
            default: false,
        },
        session: {
            type: Object,
            default: () => {}
        },
        items: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
        },
        hide_columns: {
            type: Array,
            default: () => []
        },
        no_items_text: {
            type: String
        },
        pagination: {
            type: Boolean,
            default: true,
        },
        sortable: {
            type: Boolean,
            default: false,
        },
        show_deselect_controls: {
            type: Boolean,
            default: false,
        },
        selected_comparable_ids: {
            type: Array,
            default: () => [],
        },
        selected_wide_set_ids: {
            type: Array,
            default: () => [],
        },
    },
    components: {
        HelpIcon,
        Loading,
        Pagination,
        "results-row": ResultsRow,
        "main-property-row": MainPropertyRow,
    },
    data: function () {
        return {
            // Paginator
            page: 1,
            items_per_page: 20,

            sort_key: "",

            uncollapsed_transaction_ids: [],
            additional_transaction_data: {},
        }
    },
    methods: {
        // Sorting methods
        sortBy: function(sort_key) {
            if(this.sort_key===sort_key) {
                this.sort_key = "-"+sort_key;
            } else if(this.sort_key==="-"+sort_key) {
                this.sort_key = null;
            } else{
                this.sort_key = sort_key;
            }
        },
        getSortedClass: function(sort_key) {
            if(!sort_key || !this.sortable) {return ""}
            let cls = "sorting";
            if (this.sort_key===sort_key) {
                cls+=" sorting-asc";
            } else if (this.sort_key==="-"+sort_key) {
                cls+=" sorting-desc";
            }
            return cls;
        },
        getNestedValue: function(obj, path) {
            if (!path) {return null;}
            const keys = path.split(".");
            let value = obj;
            for (let i = 0; i < keys.length; i++) {
                if (value === null || value === undefined) {
                    return null;
                }
                value = value[keys[i]];
            }
            return value;
        },
        
        // Fetch and expand transaction items methods
        toggleAdditionalTransactionitems: async function(transaction_id){
            if(this.uncollapsed_transaction_ids.includes(transaction_id)) {
                Utils.removeFromArray(this.uncollapsed_transaction_ids, transaction_id);
            } else {
                Utils.addToArray(this.uncollapsed_transaction_ids, transaction_id);
            }
            if(!this.additional_transaction_data[transaction_id]) {
                Vue.set(this.additional_transaction_data, transaction_id, "loading");
                this.fetchTransactionItems(transaction_id);
            }
        },
        getTransactionItems: function(transaction_id, exclude_id){
            let items = this.additional_transaction_data[transaction_id];
            if (Array.isArray(items)) {return items.filter(item => item.id != exclude_id);}
            return items;
        },
        fetchTransactionItems: function(transaction_id) {
            ApiService
                .get("market/api/search", { params: {
                    transaction_id: transaction_id
                }})
                .then(response => {
                    Vue.set(this.additional_transaction_data, transaction_id, response.data) 
                })
        },
        showAdditionalItems: function(transaction_id) {
            return this.uncollapsed_transaction_ids.includes(transaction_id);
        },
    },
    computed: {
        has_selected_items: function() {
            return this.selected_comparable_ids.length > 0 || this.selected_wide_set_ids.length > 0;
        },
        comparable_checkbox_class: function() {
            if (this.selected_comparable_ids.length > 0) {
                return "fa-check-square-o";
            }
            return "fa-square-o";
        },
        wide_set_checkbox_class: function() {
            if (this.selected_wide_set_ids.length > 0) {
                return "fa-check-square-o";
            }
            return "fa-square-o";
        },
        columns: function() {
            // either return unit or parcel columns
            let all_columns = []
            if (!this.session || !this.session.main_property || !this.session.main_property.real_estate) {
                return all_columns;
            }

            if (this.session.main_property.real_estate.is_parcel) {
                all_columns = all_parcel_columns;
            } else {
                all_columns = all_unit_columns;
            }
            return all_columns
                .filter(obj => !this.hide_columns.includes(obj.key))
                .filter(obj => this.getCountryPlugin("COMPARABLE_ENABLED_COLUMNS").includes(obj.key));
        },
        sortedItems: function() {
            if( !this.sort_key ) {
                return this.items;
            }
            let sort_order = 1;
            let sort_key = this.sort_key;
            if(sort_key[0] === "-") { // Descending
                sort_order = -1;
                sort_key = sort_key.slice(1);
            }
            return [...this.items].sort((a, b) => {
                let a_val = this.getNestedValue(a, sort_key);
                let b_val = this.getNestedValue(b, sort_key);
                if (a_val === b_val) {return 0;}
                if (a_val === null || a_val === undefined) {return 1;}
                if (b_val === null || b_val === undefined) {return -1;}
                if (a_val < b_val) {return -1 * sort_order;}
                if (a_val > b_val) {return 1 * sort_order;}
            });
        },
        paginated_items: function() {
            let items = this.sortedItems;
            if(this.pagination) {
                let start = (this.page - 1) * this.items_per_page;
                return items.slice(start, start + this.items_per_page);
            } else {
                return items;
            }
        }
    },
}
</script>

<style>
    /* Pagination */
    p.VuePagination__count, li.VuePagination__pagination-item-first-page, li.VuePagination__pagination-item-last-page {
        display: none!important;
    }
</style>

<style scoped>
    th.sorting::after {
        content: "\f0dc";
        font-family: fontawesome;
        color: rgba(50, 50, 50, .5);
        float: none;
        padding-left: 10px;
    }
    th.sorting-asc::after {
        content: "\f0de";
    }
    th.sorting-desc::after {
        content: "\f0dd";
    }
    table {
        font-size: 14px;
        font-family: Rubik, sans-serif;
        font-weight: 200;
    }
</style>